import z from "zod";

import type { PdfViewerTool, PdfViewerZoom } from "./PdfViewer.types";
import { PDF_VIEWER_TOOLS, PDF_VIEWER_ZOOM_MODES } from "./constants";

const TOOL_NAMES = new Set<string>(PDF_VIEWER_TOOLS);

const ZOOM_MODES = new Set<string>(PDF_VIEWER_ZOOM_MODES);

const booleanAttribute = z
  .string()
  .optional()
  .transform((value) => value !== undefined && value !== "false");

export const pdfViewerDataSchema = z.object({
  src: z.string(),
  name: z.string().optional(),
  password: z.string().optional(),
  defaultPage: z.coerce.number().int().positive().optional(),
  height: z.string().optional(),
  thumbnails: booleanAttribute,
  defaultZoom: z
    .string()
    .optional()
    .describe(
      "A zoom mode (automatic, fit-page, fit-width) or a scale factor such " +
        "as 1.5. An unknown value falls back to the component default.",
    )
    .transform((value): PdfViewerZoom | undefined => {
      if (value === undefined) {
        return undefined;
      }

      if (ZOOM_MODES.has(value)) {
        return value as PdfViewerZoom;
      }

      const scale = Number(value);

      return Number.isFinite(scale) && scale > 0 ? scale : undefined;
    }),
  tools: z
    .string()
    .optional()
    .describe(
      "Comma-separated tool names, in the order the toolbar shows them. An " +
        "empty attribute hides the toolbar. Unknown names are dropped.",
    )
    .transform((value) =>
      value === undefined
        ? undefined
        : (value
            .split(",")
            .map((tool) => tool.trim())
            .filter((tool) => TOOL_NAMES.has(tool)) as PdfViewerTool[]),
    ),
});

export type PdfViewerDataProps = z.infer<typeof pdfViewerDataSchema>;
